import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type { UpdateIssueAssessmentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateIssueAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

import { coerceToArray, type UpdateIssueAssessmentInput } from './types';

export const useUpdateIssueAssessment = createMutationHook({
  name: 'updateIssueAssessment',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.issueAssessment.update.mutationOptions({ onSuccess }),
  // OriginalTimestamp IS forwarded to the tRPC path (COI-624). It used to be
  // stripped here on the belief that "optimistic concurrency lives on the
  // legacy GraphQL path only" — that was the parity regression: v2 pinned the
  // update to `ModifiedAtTimestamp = OriginalTimestamp` and raised
  // ModifiedSinceLastView, so dropping it let two editors silently overwrite
  // each other.
  mapInput: (
    variables: UpdateIssueAssessmentInput
  ): UpdateIssueAssessmentInput => {
    const { ThirdPartyResponsibleIds, ...scalarVariables } = variables;

    return {
      ...scalarVariables,
      TagTypeIds: coerceToArray(variables.TagTypeIds),
      DepartmentTypeIds: coerceToArray(variables.DepartmentTypeIds),
      RegulationsBreachedIds: coerceToArray(variables.RegulationsBreachedIds),
      AssociatedControlIds: coerceToArray(variables.AssociatedControlIds),
      PoliciesBreachedIds: coerceToArray(variables.PoliciesBreachedIds),
      ...(ThirdPartyResponsibleIds != null
        ? {
            ThirdPartyResponsibleIds: coerceToArray(ThirdPartyResponsibleIds),
          }
        : {}),
    };
  },
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    // COI-889: router-level keys — COI-471 parity for the Apollo twin's `issue`
    // and `issue_assessment` evictions (the register embeds the assessment).
    trpc.frontend.issue.pathKey(),
    trpc.frontend.issueAssessment.pathKey(),
    // COI-471 parity: Apollo twin also evicts `control` and `tag_type`.
    trpc.frontend.control.pathKey(),
    trpc.frontend.tag.pathKey(),
    // COI-889: the ribbon's issue tile excludes issues whose assessment is
    // Closed (`getRibbonIssues` base condition: no assessment OR
    // `assessment.Status != closed`), so writing the assessment Status moves
    // the count.
    trpc.frontend.myItems.pathKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are success.
  // Echo the input Id on the CR branch (the entity is unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string }>,
    input: UpdateIssueAssessmentInput
  ): UpdateIssueAssessmentMutation => ({
    updateChildIssueAssessment: {
      __typename: 'IdOutput',
      Id: unwrapApprovalGated(result, { Id: input.Id }).Id,
    },
  }),
  graphqlDocument: UpdateIssueAssessmentDocument,
  graphqlVariables: (variables: UpdateIssueAssessmentInput) => ({
    ...variables,
  }),
  evictFields: [
    'issue',
    'tag_type',
    'issue_assessment',
    'issue_parent',
    'control',
    'issue_assessment_aggregate',
    'issue_assessment_audit',
  ],
  refetchQueries: [namedOperations.Query.getIssueById],
});
